// src/pages/Setup.jsx
import React, { useState, useEffect, useRef } from "react";
import { User } from "@/api/entities";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { saveToR2 } from "@/api/r2";
import PushSetupPanel from "@/components/PushSetupPanel"; // pannello notifiche

const steps = [
  { id: 1, title: "Il tuo nome" },
  { id: 2, title: "Dati demografici" },
  { id: 3, title: "Abitudini di fumo" },
  { id: 4, title: "Dettagli consumo" },
  { id: 5, title: "Come ci hai trovato?" }, // ✅ NUOVO step
  { id: 6, title: "Promemoria & check-in" }, // ✅ vecchio step 5 spostato
];

// (rimane, ma non lo usiamo più nello step 5)
const TIME_CHOICES = [
  "08:00",
  "10:00",
  "12:00",
  "15:00",
  "18:00",
  "19:00",
  "20:00",
  "21:00",
  "22:00",
];

function isValidEmail(v = "") {
  return /\S+@\S+\.\S+/.test(v.trim());
}
function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/* ---------- UTILITY ORA+MINUTI ---------- */
const hourOptions = (start, end) => {
  const arr = [];
  for (let h = start; h <= end; h++) arr.push(String(h).padStart(2, "0"));
  return arr;
};
const minuteOptions = () =>
  Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const parseTime = (hhmm) => {
  const [h = "00", m = "00"] = String(hhmm || "").split(":");
  return { h, m };
};
const combineTime = (h, m) =>
  `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;

/* ✅ PATCH MINIMA: helper timeout per evitare hang su iPhone/Safari quando SW non diventa "ready" */
function withTimeout(promise, ms, fallback = null) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

/* ✅ dopo il Setup sincronizza l'eventuale subscription push verso /api/push-subscribe */
async function syncPushAfterSetup(prefs) {
  try {
    if (!("serviceWorker" in navigator)) return;

    // ✅ PATCH MINIMA: su iPhone/Safari può NON arrivare mai a "ready" → non bloccare il setup
    const reg = await withTimeout(navigator.serviceWorker.ready, 2500, null);
    if (!reg) {
      console.warn("[setup] SW not ready (timeout) → skip push sync");
      return;
    }

    const sub = await reg.pushManager.getSubscription();
    if (!sub) return; // l'utente potrebbe non aver attivato la push nel pannello

    let userId = "anon";
    try {
      const me = await User.me();
      userId = me.id || me.uid || me.email || "anon";
    } catch {
      // se fallisce, usiamo comunque "anon"
    }

    const tz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome";

    await fetch("/api/push-subscribe", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        userId,
        subscription: sub, // serializzato dal browser
        tz,
        ua: navigator.userAgent,
        prefs, // orari scelti nel setup (facoltativo lato server)
      }),
    });
    console.log("[setup] push subscription sincronizzata");
  } catch (e) {
    console.warn("[setup] push sync skipped:", e);
  }
}

/* ✅ CHANGE MINIMA RICHIESTA: salva anche gli orari su /api/push-settings (come fa Impostazioni → Salva) */
async function savePushSettingsAfterSetup(prefs) {
  try {
    let userId = "anon";
    try {
      const me = await User.me();
      userId = me.id || me.uid || me.email || "anon";
    } catch {}

    const tz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Rome";

    const payload = {
      userId,
      tz,
      settings: {
        notification_checkin_enabled: true,
        notification_checkin_time: prefs?.checkin || "19:00",
        notification_exercises_enabled: true,
        notification_exercise_time: prefs?.exercise || "10:00",
      },
    };

    const res = await fetch("/api/push-settings", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      console.warn("[setup] push-settings failed:", res.status, t);
      return;
    }

    console.log("[setup] push settings salvate ✅");
  } catch (e) {
    console.warn("[setup] push-settings skipped:", e);
  }
}

/* ✅ FIX PWA ANDROID: salva lo stato del setup per evitare reset quando si apre /tutorial */
const SETUP_DRAFT_KEY = "rb_setup_draft_v1";

function safeReadDraft() {
  try {
    if (typeof window === "undefined") return null;

    // ✅ priorità: localStorage (più stabile su PWA) → fallback sessionStorage
    const raw =
      window.localStorage?.getItem(SETUP_DRAFT_KEY) ||
      window.sessionStorage?.getItem(SETUP_DRAFT_KEY);

    if (!raw) return null;
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") return null;

    // opzionale: se manca ts va bene lo stesso
    return parsed;
  } catch {
    return null;
  }
}

function safeWriteDraft(payload) {
  try {
    if (typeof window === "undefined") return;
    const raw = JSON.stringify(payload);

    // ✅ scrivi in entrambi (massima compatibilità)
    try {
      window.localStorage?.setItem(SETUP_DRAFT_KEY, raw);
    } catch {}
    try {
      window.sessionStorage?.setItem(SETUP_DRAFT_KEY, raw);
    } catch {}
  } catch {}
}

function safeClearDraft() {
  try {
    if (typeof window === "undefined") return;
    try {
      window.localStorage?.removeItem(SETUP_DRAFT_KEY);
    } catch {}
    try {
      window.sessionStorage?.removeItem(SETUP_DRAFT_KEY);
    } catch {}
  } catch {}
}

export default function SetupPage() {
  const navigate = useNavigate();

  /* ✅ FIX DEFINITIVO:
     vai al tutorial SOLO se:
     - non è stato visto (localStorage OR sessionStorage)
     - E non esiste già un draft (evita reset/loop su PWA) */
  useEffect(() => {
    let seen = false;
    try {
      seen =
        window.localStorage?.getItem("rb_tutorial_done") === "1" ||
        window.sessionStorage?.getItem("rb_tutorial_done") === "1";
    } catch {}
    const hasDraft = !!safeReadDraft();
    if (!seen && !hasDraft) navigate("/tutorial", { replace: true });
  }, [navigate]);

  // ✅ default formData
  const DEFAULT_FORM_DATA = {
    // Step 1
    firstName: "",
    lastName: "",
    email: "",
    // Step 2
    age_range: "",
    gender: "",
    smoking_years: "",
    // Notifiche
    notification_checkin_time: "19:00",
    notification_exercise_time: "10:00",
    // Tipologia + dettagli
    smoking_type: "cigarettes",
    cigarettes_per_day: "",
    pack_price: "",
    cigarettes_per_pack: "20",
    lighters_per_week: "1",
    tobacco_packs_per_week: "",
    tobacco_pack_price: "",
    paper_packs_per_week: "1",
    paper_pack_price: "1.00",
    filter_packs_per_week: "1",
    filter_pack_price: "2.00",
    // ✅ NUOVO campo
    found_via: "",
  };

  // ✅ inizializzazione: ripristina SUBITO (niente flicker)
  const [currentStep, setCurrentStep] = useState(() => {
    const d = safeReadDraft();
    const n = Number(d?.currentStep);
    if (!Number.isFinite(n)) return 1;
    return Math.min(Math.max(n, 1), steps.length);
  });

  const [formData, setFormData] = useState(() => {
    const d = safeReadDraft();
    const fromDraft =
      d && d.formData && typeof d.formData === "object" ? d.formData : {};
    return { ...DEFAULT_FORM_DATA, ...fromDraft };
  });

  const [isSaving, setIsSaving] = useState(false);

  // ✅ ref per flush draft “istantaneo” (PWA: pagehide/visibilitychange)
  const draftRef = useRef({ currentStep: 1, formData: DEFAULT_FORM_DATA });
  useEffect(() => {
    draftRef.current = { currentStep, formData };
  }, [currentStep, formData]);

  useEffect(() => {
    const flush = () => {
      const snap = draftRef.current || {};
      safeWriteDraft({
        currentStep: snap.currentStep,
        formData: snap.formData,
        ts: Date.now(),
      });
    };

    const onVis = () => {
      if (document.hidden) flush();
    };

    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // ✅ AUTOSAVE draft (debounce leggero)
  useEffect(() => {
    const t = setTimeout(() => {
      safeWriteDraft({
        currentStep,
        formData,
        ts: Date.now(),
      });
    }, 120);
    return () => clearTimeout(t);
  }, [currentStep, formData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length) setCurrentStep(currentStep + 1);
    else handleSave();
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // 1) Normalizza i dati dell’utente da salvare nel profilo app
      const dataToSave = {
        full_name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email.trim().toLowerCase(),
        age_range: formData.age_range,
        gender: formData.gender,
        smoking_years: formData.smoking_years,
        setup_completed: true,
        // ✅ salviamo la sorgente
        found_via: formData.found_via || null,
        // notifiche
        notification_checkin_time: formData.notification_checkin_time,
        notification_exercise_time: formData.notification_exercise_time,
        // fumo
        smoking_type: formData.smoking_type,
        lighters_per_week: toNum(formData.lighters_per_week),
      };

      if (formData.smoking_type === "cigarettes") {
        dataToSave.cigarettes_per_day = toNum(formData.cigarettes_per_day);
        dataToSave.pack_price = toNum(formData.pack_price);
        dataToSave.cigarettes_per_pack = toNum(formData.cigarettes_per_pack);
      } else {
        dataToSave.tobacco_packs_per_week = toNum(
          formData.tobacco_packs_per_week
        );
        dataToSave.tobacco_pack_price = toNum(formData.tobacco_pack_price);
        dataToSave.paper_packs_per_week = toNum(formData.paper_packs_per_week);
        dataToSave.paper_pack_price = toNum(formData.paper_pack_price);
        dataToSave.filter_packs_per_week = toNum(
          formData.filter_packs_per_week
        );
        dataToSave.filter_pack_price = toNum(formData.filter_pack_price);
      }

      // 2) Salva profilo utente
      await User.updateMyUserData(dataToSave);

      // 3) Log “intro” su R2 (se offline verrà accodato)
      await saveToR2("intro", {
        data: {
          ...dataToSave,
          raw_form: formData,
          completed_at: new Date().toISOString(),
        },
        meta: { source: "setup", version: "v2" },
      });

      // 4) Sync push NON bloccante (anche se fallisce su iPhone il setup è già salvato)
      try {
        await syncPushAfterSetup({
          checkin: formData.notification_checkin_time,
          exercise: formData.notification_exercise_time,
        });
      } catch (e) {
        console.warn("Sync push after setup failed, ignoring", e);
      }

      // ✅ CHANGE MINIMA RICHIESTA: salva anche gli orari su /api/push-settings (come Impostazioni → Salva)
      await savePushSettingsAfterSetup({
        checkin: formData.notification_checkin_time,
        exercise: formData.notification_exercise_time,
      });

      // ✅ pulizia draft: setup completato
      safeClearDraft();

      // 5) Vai in Dashboard
      navigate(createPageUrl("Dashboard"));
    } catch (error) {
      console.error("Errore salvataggio dati setup:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return (
          !formData.firstName.trim() ||
          !formData.lastName.trim() ||
          !isValidEmail(formData.email)
        );
      case 2:
        return !formData.age_range || !formData.gender || !formData.smoking_years;
      case 3:
        return !formData.smoking_type;
      case 4:
        if (formData.smoking_type === "cigarettes") {
          return !(toNum(formData.cigarettes_per_day) > 0 && toNum(formData.pack_price) > 0);
        }
        if (formData.smoking_type === "tobacco") {
          return !(toNum(formData.tobacco_packs_per_week) > 0 && toNum(formData.tobacco_pack_price) > 0);
        }
        return true;
      case 5: // ✅ nuova validazione: obbligatoria la scelta della sorgente
        return !formData.found_via;
      case 6: // ✅ vecchia validazione step 5
        return !formData.notification_checkin_time || !formData.notification_exercise_time;
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-center">Come ti chiami?</h2>
            <div>
              <Label htmlFor="firstName">Nome</Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                autoComplete="given-name"
                placeholder="Mario"
              />
            </div>
            <div>
              <Label htmlFor="lastName">Cognome</Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                autoComplete="family-name"
                placeholder="Rossi"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                inputMode="email"
                placeholder="nome@esempio.com"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={!formData.email || isValidEmail(formData.email) ? "" : "border-red-500"}
                autoComplete="email"
              />
              {!(!formData.email || isValidEmail(formData.email)) && (
                <p className="text-xs text-red-600 mt-1">Inserisci un’email valida.</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-center">Parlaci di te</h2>
            <div>
              <Label>Quanti anni hai?</Label>
              <RadioGroup
                value={formData.age_range}
                onValueChange={(v) => handleInputChange("age_range", v)}
                className="grid grid-cols-2 gap-2 mt-2"
              >
                {[
                  "sotto i 20",
                  "20-30",
                  "30-40",
                  "40-50",
                  "50-60",
                  "60-70",
                  "dopo i 70",
                ].map((age) => (
                  <Label
                    key={age}
                    htmlFor={`age-${age}`}
                    className={`flex items-center space-x-2 border p-3 rounded-lg cursor-pointer transition-all ${
                      formData.age_range === age
                        ? "bg-blue-100 border-blue-400"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <RadioGroupItem value={age} id={`age-${age}`} />{" "}
                    <span>{age}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label>Genere</Label>
              <RadioGroup
                value={formData.gender}
                onValueChange={(v) => handleInputChange("gender", v)}
                className="flex gap-4 mt-2"
              >
                {["maschio", "femmina", "altro"].map((gender) => (
                  <Label
                    key={gender}
                    htmlFor={`gender-${gender}`}
                    className={`flex items-center space-x-2 border p-3 rounded-lg cursor-pointer transition-all ${
                      formData.gender === gender
                        ? "bg-blue-100 border-blue-400"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <RadioGroupItem value={gender} id={`gender-${gender}`} />{" "}
                    <span>{gender}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div>
              <Label>Da quanti anni fumi?</Label>
              <RadioGroup
                value={formData.smoking_years}
                onValueChange={(v) => handleInputChange("smoking_years", v)}
                className="grid grid-cols-2 gap-2 mt-2"
              >
                {["0-5", "5-10", "10-15", "15-20", "più di 20"].map((years) => (
                  <Label
                    key={years}
                    htmlFor={`years-${years}`}
                    className={`flex items-center space-x-2 border p-3 rounded-lg cursor-pointer transition-all ${
                      formData.smoking_years === years
                        ? "bg-blue-100 border-blue-400"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <RadioGroupItem value={years} id={`years-${years}`} />{" "}
                    <span>{years} anni</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-center">Che tipo di fumatore sei?</h2>
            <RadioGroup
              value={formData.smoking_type}
              onValueChange={(v) => handleInputChange("smoking_type", v)}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <Label
                htmlFor="cigarettes"
                className={`flex flex-col items-center justify-center space-y-2 border p-6 rounded-lg cursor-pointer transition-all ${
                  formData.smoking_type === "cigarettes"
                    ? "bg-blue-100 border-blue-400"
                    : "hover:bg-gray-50"
                }`}
              >
                <RadioGroupItem value="cigarettes" id="cigarettes" className="sr-only" />
                <span className="text-4xl">🚬</span>
                <span className="font-semibold">Sigarette</span>
              </Label>
              <Label
                htmlFor="tobacco"
                className={`flex flex-col items-center justify-center space-y-2 border p-6 rounded-lg cursor-pointer transition-all ${
                  formData.smoking_type === "tobacco"
                    ? "bg-blue-100 border-blue-400"
                    : "hover:bg-gray-50"
                }`}
              >
                <RadioGroupItem value="tobacco" id="tobacco" className="sr-only" />
                <span className="text-4xl">📜</span>
                <span className="font-semibold">Tabacco</span>
              </Label>
            </RadioGroup>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-center">Dettagli sul consumo</h2>
            <AnimatePresence mode="wait">
              {formData.smoking_type === "cigarettes" ? (
                <motion.div
                  key="cigarettes"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <Label htmlFor="cigarettes_per_day">Quante sigarette fumavi al giorno?</Label>
                    <Input
                      id="cigarettes_per_day"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      placeholder="es. 15"
                      value={formData.cigarettes_per_day}
                      onChange={(e) => handleInputChange("cigarettes_per_day", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="pack_price">Prezzo del pacchetto (€)</Label>
                    <Input
                      id="pack_price"
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="es. 5.50"
                      value={formData.pack_price}
                      onChange={(e) => handleInputChange("pack_price", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lighters_per_week_cigarettes">Accendini a settimana (circa)</Label>
                    <Input
                      id="lighters_per_week_cigarettes"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      placeholder="es. 1"
                      value={formData.lighters_per_week}
                      onChange={(e) => handleInputChange("lighters_per_week", e.target.value)}
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="tobacco"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div>
                    <Label htmlFor="tobacco_packs_per_week">Pacchi di tabacco a settimana</Label>
                    <Input
                      id="tobacco_packs_per_week"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      placeholder="es. 2"
                      value={formData.tobacco_packs_per_week}
                      onChange={(e) => handleInputChange("tobacco_packs_per_week", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="tobacco_pack_price">Prezzo per pacco di tabacco (€)</Label>
                    <Input
                      id="tobacco_pack_price"
                      type="number"
                      step="0.1"
                      min="0"
                      placeholder="es. 6.00"
                      value={formData.tobacco_pack_price}
                      onChange={(e) => handleInputChange("tobacco_pack_price", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label htmlFor="lighters_per_week_tobacco">Accendini a settimana (circa)</Label>
                    <Input
                      id="lighters_per_week_tobacco"
                      type="number"
                      inputMode="numeric"
                      min="0"
                      placeholder="es. 1"
                      value={formData.lighters_per_week}
                      onChange={(e) => handleInputChange("lighters_per_week", e.target.value)}
                    />
                  </div>

                  {/* SOLO TABACCO: cartine & filtri */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="paper_pack_price">Prezzo pacco cartine (€)</Label>
                      <Input
                        id="paper_pack_price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="es. 1.00"
                        value={formData.paper_pack_price}
                        onChange={(e) => handleInputChange("paper_pack_price", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="paper_packs_per_week">Pacchi cartine alla settimana</Label>
                      <Input
                        id="paper_packs_per_week"
                        type="number"
                        inputMode="numeric"
                        min="0"
                        placeholder="es. 1"
                        value={formData.paper_packs_per_week}
                        onChange={(e) => handleInputChange("paper_packs_per_week", e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="filter_pack_price">Prezzo pacco filtri (€)</Label>
                      <Input
                        id="filter_pack_price"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="es. 2.00"
                        value={formData.filter_pack_price}
                        onChange={(e) => handleInputChange("filter_pack_price", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="filter_packs_per_week">Pacchi filtri alla settimana</Label>
                      <Input
                        id="filter_packs_per_week"
                        type="number"
                        inputMode="numeric"
                        min="0"
                        placeholder="es. 1"
                        value={formData.filter_packs_per_week}
                        onChange={(e) => handleInputChange("filter_packs_per_week", e.target.value)}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );

      case 5: {
        const OPTIONS = [
          "Instagram",
          "Facebook",
          "Google",
          "Youtube",
          "Passaparola",
          "Intelligenza artificiale",
        ];
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-center">Come ci hai trovato?</h2>
            <RadioGroup
              value={formData.found_via}
              onValueChange={(v) => handleInputChange("found_via", v)}
              className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2"
            >
              {OPTIONS.map((src) => {
                const id = `found-${src.toLowerCase().replace(/\s+/g, "-")}`;
                return (
                  <Label
                    key={src}
                    htmlFor={id}
                    className={`flex items-center space-x-2 border p-3 rounded-lg cursor-pointer transition-all ${
                      formData.found_via === src
                        ? "bg-blue-100 border-blue-400"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    <RadioGroupItem value={src} id={id} /> <span>{src}</span>
                  </Label>
                );
              })}
            </RadioGroup>
            {!formData.found_via && (
              <p className="text-xs text-red-600">
                Seleziona un&apos;opzione per continuare.
              </p>
            )}
          </div>
        );
      }

      case 6: {
        const ex = parseTime(formData.notification_exercise_time);
        const ck = parseTime(formData.notification_checkin_time);

        const isIOS =
          typeof navigator !== "undefined" &&
          /iPad|iPhone|iPod/i.test(navigator.userAgent);

        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-center">Promemoria & check-in</h2>
            <p className="text-center text-gray-500">Imposta gli orari e attiva le notifiche.</p>

            <div className="space-y-3">
              <div className="border rounded-lg p-3 bg-white">
                <p className="text-sm">
                  <strong>Importante:</strong>{" "}
                  {isIOS
                    ? "su iPhone le notifiche funzionano solo se installi l’app (Aggiungi alla schermata Home)."
                    : "se non lo hai già fatto scarica l’app per proseguire."}
                </p>
                <div className="mt-2 flex flex-wrap gap-2 items-center">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      // ✅ salva draft prima di andare al tutorial
                      safeWriteDraft({
                        currentStep,
                        formData,
                        ts: Date.now(),
                      });

                      // ✅ FIX: replace riduce history weird su PWA
                      navigate("/tutorial", { replace: true });
                    }}
                  >
                    Vai al tutorial
                  </Button>

                  <a
                    href="https://wa.me/393519743127"
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm underline"
                  >
                    Assistenza WhatsApp: +39 351 974 3127
                  </a>
                </div>
              </div>

              <div className="border rounded-lg p-3 bg-white">
                <p className="text-sm text-red-600">
                  <strong>Per poter procedere:</strong> premi{" "}
                  <strong>“Attiva notifiche”</strong> e poi{" "}
                  <strong>“Notifica di prova”</strong>. Se non lo fai, il setup
                  può bloccarsi su “Salvataggio…”.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <Label>Promemoria esercizi</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={ex.h}
                    onValueChange={(h) =>
                      handleInputChange(
                        "notification_exercise_time",
                        combineTime(h, parseTime(formData.notification_exercise_time).m)
                      )
                    }
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions(7, 21).map((h) => (
                        <SelectItem key={h} value={h}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="opacity-60">:</span>
                  <Select
                    value={ex.m}
                    onValueChange={(m) =>
                      handleInputChange(
                        "notification_exercise_time",
                        combineTime(parseTime(formData.notification_exercise_time).h, m)
                      )
                    }
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {minuteOptions().map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <Label>Check-in giornaliero</Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={ck.h}
                    onValueChange={(h) =>
                      handleInputChange(
                        "notification_checkin_time",
                        combineTime(h, parseTime(formData.notification_checkin_time).m)
                      )
                    }
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions(18, 22).map((h) => (
                        <SelectItem key={h} value={h}>
                          {h}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <span className="opacity-60">:</span>
                  <Select
                    value={ck.m}
                    onValueChange={(m) =>
                      handleInputChange(
                        "notification_checkin_time",
                        combineTime(parseTime(formData.notification_checkin_time).h, m)
                      )
                    }
                  >
                    <SelectTrigger className="w-[110px]">
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {minuteOptions().map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <PushSetupPanel schedule={formData.notification_checkin_time} />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">
          Benvenuto in RespirareBene
        </h1>
        <p className="text-center text-gray-600 mb-8">
          Completiamo insieme il tuo profilo per iniziare.
        </p>

        <Progress value={(currentStep / steps.length) * 100} className="mb-8 w-full" />

        <Card>
          <CardContent className="p-8 min-h-[350px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>

        <div className="mt-8 flex justify-between items-center">
          <Button variant="outline" onClick={handleBack} disabled={currentStep === 1}>
            Indietro
          </Button>
          <Button onClick={handleNext} disabled={isNextDisabled() || isSaving}>
            {isSaving
              ? "Salvataggio..."
              : currentStep === steps.length
              ? "Completa Setup"
              : "Avanti"}
          </Button>
        </div>
      </div>
    </div>
  );
}
